import { ref } from 'vue';
import { useServerConfig } from './useServerConfig';

export function useGetInvoices() {
  const { serverAddress } = useServerConfig();
  const invoices = ref([]);
  const error = ref(null);
  const loading = ref(false);
  const pdfLoading = ref(new Set());

  const openPdfInNewTab = async (invoiceId) => {
    try {
      // Add invoiceId to loading set
      pdfLoading.value.add(invoiceId);

      const response = await fetch(`${serverAddress.value}/qb/getpdf/${invoiceId}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw response.status;
      }

      const data = await response.json();
      if (!data.success) throw data.error;

      // Convert base64 to blob and open in new tab
      const byteCharacters = atob(data.pdf);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice ${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error opening PDF:', err);
      error.value = 'There was an error opening the PDF.';
      alert('There was an error opening the PDF. Please try again later.');
    } finally {
      // Remove invoiceId from loading set
      pdfLoading.value.delete(invoiceId);
    }
  };

  const fetchInvoices = async () => {
    loading.value = true;
    error.value = null;
    try {
      const response = await fetch(`${serverAddress.value}/qb/getinvoices`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw response.status;
      }
      const data = await response.json();
      if (!data.success) throw data.error;
      invoices.value = data.invoices;
    } catch (err) {
      console.log(err);
      if (err == '401') {
        // Handle unauthorized
      } else {
        error.value = 'There was an error fetching invoices. Please try again.';
      }
    } finally {
      loading.value = false;
    }
  };

  return {
    invoices,
    error,
    loading,
    pdfLoading,
    fetchInvoices,
    openPdfInNewTab,
  };
}
